import type { Archive } from '../types';

/** A heartbeat older than this means the recrawl subprocess is presumed
 *  dead. Recrawl writes progress every batch (~200 snapshots) which is a
 *  few seconds even on slow R2 reads, so this threshold gives plenty of
 *  slack while still catching killed/OOM'd subprocesses. */
const STALE_AFTER_MS = 5 * 60 * 1000;

export type RecrawlState =
  | { state: 'idle' }
  | {
      state: 'running';
      startedAt: string;
      scanned: number;
      followups: number;
    }
  | {
      state: 'stalled';
      startedAt: string;
      progressAt: string | null;
      staleForMs: number;
      scanned: number;
      followups: number;
    }
  | {
      state: 'done';
      startedAt: string;
      finishedAt: string;
      scanned: number;
      followups: number;
    };

export function recrawlState(
  archive: Pick<
    Archive,
    | 'recrawl_started_at'
    | 'recrawl_finished_at'
    | 'recrawl_progress_at'
    | 'recrawl_snapshots_scanned'
    | 'recrawl_followups_enqueued'
  >,
): RecrawlState {
  const startedAt = archive.recrawl_started_at;
  if (!startedAt) return { state: 'idle' };

  const scanned = archive.recrawl_snapshots_scanned ?? 0;
  const followups = archive.recrawl_followups_enqueued ?? 0;
  const finishedAt = archive.recrawl_finished_at;

  if (finishedAt) {
    return { state: 'done', startedAt, finishedAt, scanned, followups };
  }

  const progressAt = archive.recrawl_progress_at ?? null;
  const now = Date.now();
  const lastSeen = progressAt ? Date.parse(progressAt) : Date.parse(startedAt);
  const staleForMs = now - lastSeen;
  if (staleForMs > STALE_AFTER_MS) {
    return { state: 'stalled', startedAt, progressAt, staleForMs, scanned, followups };
  }
  return { state: 'running', startedAt, scanned, followups };
}
